/**
 * CopyButton — copies a value to the clipboard with visual feedback.
 */

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface CopyButtonProps {
  /** The text to copy to the clipboard. */
  value: string;
  /** Optional label for the button. */
  label?: string;
}

export const CopyButton = ({ value, label }: CopyButtonProps) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const displayLabel = label ?? t('common.copy');

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for insecure contexts
      const textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [value]);

  return (
    <button
      className="btn btn--secondary btn--sm copy-container__btn"
      onClick={handleCopy}
      aria-label={copied ? t('common.copied') : `${displayLabel}`}
      type="button"
    >
      {copied ? (
        <>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          {t('common.copied')}
        </>
      ) : (
        <>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
          {displayLabel}
        </>
      )}
    </button>
  );
};
